import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { api, formatApiError } from "@/lib/api";
import { MessageSquare, ArrowRight, ArrowLeft, Loader2, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 grid place-items-center p-6 relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-emerald-500/10 blur-3xl" />
      </div>
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shadow-[0_0_30px_rgba(16,185,129,0.45)]">
            <MessageSquare className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="font-bold text-2xl leading-tight">Feedback Hub</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">recuperar acesso</div>
          </div>
        </div>

        <Card className="bg-[#111827] border-slate-800/80 p-6">
          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold">Esqueci minha senha</h2>
                <p className="text-sm text-slate-400 mt-1">Informe o e-mail cadastrado. Enviaremos um link válido por 15 minutos.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="forgot-email-input" className="bg-slate-900/60 border-slate-800" />
              </div>
              {err && <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}
              <Button type="submit" disabled={loading} data-testid="forgot-submit-button"
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white active:scale-[0.98]">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Enviar link de redefinição
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              <Link to="/login" data-testid="forgot-back-to-login" className="flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-emerald-400 mt-2">
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar para o login
              </Link>
            </form>
          ) : (
            <div className="text-center py-6" data-testid="forgot-sent-confirmation">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-4">
                <MailCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="text-xl font-semibold">Verifique seu e-mail</h2>
              <p className="text-sm text-slate-400 mt-2">
                Se este e-mail estiver cadastrado, um link de redefinição foi enviado. O link expira em 15 minutos.
              </p>
              <Button onClick={() => nav("/login")} className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-white">
                Voltar para o login
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
